
// packages/components/src/agent-management/AgentIdentityCard.tsx
import React, { useState, useEffect } from 'react';
import { Card, Button, TextField, TextareaField, IconButton, Spinner, Alert, Modal, Tooltip } from '@dopomogai/ui';
import { useAgent } from '@dopomogai/agent-kit';
import { Edit2, User, X, Check, Lock } from 'lucide-react';
import { toast } from '@dopomogai/ui';

interface AgentIdentityCardProps {
  agentId: string;
  readOnly?: boolean;
}

export const AgentIdentityCard: React.FC<AgentIdentityCardProps> = ({ agentId, readOnly = false }) => {
  const { agent, updateAgent, isLoading, error } = useAgent(agentId);

  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editedName, setEditedName] = useState('');
  const [editedDescription, setEditedDescription] = useState('');
  const [isAvatarModalOpen, setIsAvatarModalOpen] = useState(false);

  useEffect(() => {
    if (agent) {
      setEditedName(agent.name);
      setEditedDescription(agent.description ?? '');
    }
  }, [agent]);

  const handleEnterEditMode = () => {
    if (readOnly) return;
    if (agent) {
      setEditedName(agent.name);
      setEditedDescription(agent.description ?? '');
      setIsEditing(true);
    }
  };

  const handleSaveChanges = async () => {
    if (!editedName.trim()) {
      toast.warn('Agent name cannot be empty.');
      return;
    }

    setIsSaving(true);
    try {
      await updateAgent({
        name: editedName,
        description: editedDescription,
      });
      toast.success('Agent details updated successfully!');
      setIsEditing(false);
    } catch (e: any) {
      toast.error(`Failed to update agent: ${e.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  const renderPlaceholderAvatar = () => (
    <button
      type="button"
      onClick={() => setIsAvatarModalOpen(true)}
      className="flex items-center gap-3 border-2 border-black bg-white rounded-xl p-3 hover:-translate-x-px hover:-translate-y-px hover:shadow-[2px_2px_0_0_#000] transition"
    >
      <div className="w-14 h-14 rounded-lg bg-slate-50 border border-slate-200 flex items-center justify-center text-[10px] font-semibold text-slate-400 uppercase tracking-wide">
        Avatar
      </div>
      <div className="flex-1">
        <p className="text-xs text-slate-500 leading-relaxed">
          Click to learn more
        </p>
      </div>
    </button>
  );

  const renderContent = () => {
    if (isLoading) return <div className="flex justify-center p-4"><Spinner /></div>;
    if (error) return <Alert type="error" title="Error" message={error.message} />;
    if (!agent) return <Alert type="warning" title="Not found" message="Agent data could not be loaded." />;

    if (isEditing && !readOnly) {
      return (
        <div className="space-y-4">
          {renderPlaceholderAvatar()}
          <TextField
            label="Agent name"
            value={editedName}
            onChange={(e) => setEditedName(e.target.value)}
            required
            disabled={isSaving}
          />
          <TextareaField
            label="Description / tagline"
            value={editedDescription}
            onChange={(e) => setEditedDescription(e.target.value)}
            rows={3}
            disabled={isSaving}
          />
          <div className="flex justify-end gap-2 pt-2">
            <Button
              text="Cancel"
              variant="outline"
              onClick={() => setIsEditing(false)}
              disabled={isSaving}
              icon={<X size={16} />}
            />
            <Button
              text={isSaving ? 'Saving…' : 'Save'}
              variant="primary"
              onClick={handleSaveChanges}
              disabled={isSaving}
              icon={isSaving ? undefined : <Check size={16} />}
            />
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {renderPlaceholderAvatar()}
        <div>
          <p className="text-sm font-medium text-slate-500">Agent name</p>
          <p className="text-lg font-semibold text-slate-800">{agent.name}</p>
        </div>
        <div>
          <p className="text-sm font-medium text-slate-500">Description / tagline</p>
          <p className="text-slate-600">
            {agent.description || <span className="italic text-slate-400">No description provided.</span>}
          </p>
        </div>
      </div>
    );
  };

  return (
    <Card
      title="Agent identity"
      icon={<User size={18} />}
      headerBgColor="bg-sky-100"
      actionButton={!isEditing && !isLoading && (
        readOnly ? (
          <Tooltip text="View-only">
            <span><Lock size={16} className="text-slate-500" /></span>
          </Tooltip>
        ) : (
          <IconButton
            icon={<Edit2 size={16} />}
            ariaLabel="Edit agent identity"
            variant="ghost"
            onClick={handleEnterEditMode}
          />
        )
      )}
    >
      {renderContent()}

      <Modal
        isOpen={isAvatarModalOpen}
        onClose={() => setIsAvatarModalOpen(false)}
        title="Avatars"
        size="sm"
      >
        <p className="text-sm text-slate-600">
          Custom agent avatars are coming soon. You’ll be able to upload or generate one to personalize your workspace.
        </p>
      </Modal>
    </Card>
  );
};